import { useState } from "react";
import { useRevealSearchTarget, type SearchFocus } from "@/shared/hooks/useSearchFocus";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import { ThemedSegmented } from "@/shared/components/ThemedSegmented";
import type { DiagnosisRecord } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import type { ConditionStatus } from "@/features/patient/records/api/dataPipelineApi";
import { ConditionStatusControl } from "./ConditionStatusControl";
import { cn } from "@/shared/lib/utils";
import { listCardClass } from "@/shared/lib/cardStyles";
import { LG_QUERY, useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { FoundInRecordsTag } from "@/shared/components/FoundInRecords";
import { GoldenSources, GoldenTag } from "@/features/empi/GoldenRecord";

type Diagnosis = DiagnosisRecord;
type Group = "active" | "inactive" | "resolved";

interface DiagnosisSectionProps {
  diagnoses: DiagnosisRecord[];
  isLoading?: boolean;
  /** Marks a diagnosis active / inactive / resolved; rejects with a message on failure. */
  onSetStatus?: (id: string, status: ConditionStatus) => Promise<void>;
  /** False while viewing someone else's profile: only the patient can change a status. */
  canEditStatus?: boolean;
  /** A header search result to open here (useSearchFocus.ts). */
  focus?: SearchFocus;
}

/** "Since" and "Last updated" as two labelled columns — the same on Health Concerns. */
export function SinceAndUpdated({
  since, updated, updatedLabel = "Last updated", empty,
}: {
  since: string;
  updated: string;
  /** The second column's name (Health Concerns: "Documented on"). */
  updatedLabel?: string;
  /** Shown for a missing date instead of the default "Not available". */
  empty?: string;
}) {
  const show = (value: string) => (!value && empty ? empty : formatDate(value));
  return (
    <dl className="!mb-0 grid shrink-0 grid-cols-2 gap-x-5 text-right" data-testid="since-and-updated">
      <div>
        <dt className="text-[11px] leading-4 text-[#9CA3AF]">Since</dt>
        <dd className="!mb-0 !mt-1 whitespace-nowrap text-xs font-medium leading-4 text-[#180047]">{show(since)}</dd>
      </div>
      <div>
        <dt className="text-[11px] leading-4 text-[#9CA3AF]">{updatedLabel}</dt>
        <dd className="!mb-0 !mt-1 whitespace-nowrap text-xs font-medium leading-4 text-[#180047]">{show(updated)}</dd>
      </div>
    </dl>
  );
}

// Same small pink pill as Health Concerns.
const TAG = "inline-flex items-center rounded-[3px] bg-[#F17893] px-1.5 py-[3px] text-[9px] font-semibold leading-none text-white";

const GROUPS: { key: Group; title: string; stat: string; icon: string }[] = [
  { key: "active",   title: "Active Diagnoses",   stat: "Active Diagnoses",   icon: "lucide:calendar-check-2" },
  { key: "inactive", title: "Inactive Diagnoses", stat: "Inactive Diagnoses", icon: "lucide:file-minus" },
  { key: "resolved", title: "Resolved Diagnoses", stat: "Resolved Diagnoses", icon: "lucide:square-check" },
];

const GROUP_LABEL: Record<Group, string> = { active: "Active", inactive: "Inactive", resolved: "Resolved" };

/** Active / Inactive / Resolved as shown: the hospital's, or the patient's own mark. */
function groupOf(d: Diagnosis): Group {
  return d.statusInfo.status;
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const date = new Date(dateString.slice(0, 10) + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "Today" when the date is today, otherwise "Jun 15, 2024". */
function formatRelativeDay(dateString: string): string {
  const today = new Date().toLocaleDateString("en-CA");
  return dateString.slice(0, 10) === today ? "Today" : formatDate(dateString);
}

// ── Top stat card ──────────────────────────────────────────────────────────
// Same card as Medications and Diagnostics & Imaging; clicking one lists that group.
function StatCard({ label, icon, value, onClick }: { label: string; icon: string; value: number; onClick: () => void }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      aria-label={`${label}: ${value}`}
      className="!flex !h-auto !min-h-[110px] !flex-col !items-stretch !justify-between !rounded-3xl !border-[#E5E7EB] !bg-white !px-6 !py-5 !text-left !shadow-sm hover:!-translate-y-0.5 hover:!border-[#E5E7EB] hover:!bg-white hover:!shadow-lg"
    >
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon={icon} className="h-4 w-4 !text-white" />
        </div>
        <p className="!mb-0 text-sm font-semibold text-[#111827]">{label}</p>
      </div>
      <p className="!mb-0 text-right text-3xl font-bold text-[#111827]">{value}</p>
    </Button>
  );
}

// ── List card ──────────────────────────────────────────────────────────────
function DiagnosisCard({ diagnosis, selected, onClick }: { diagnosis: Diagnosis; selected: boolean; onClick: () => void }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={cn(
        "!h-auto !w-full !whitespace-normal !rounded-xl !px-[14px] !py-4 !text-left !shadow-none",
        listCardClass(selected),
        selected && "!shadow-[0_0_0_3px_rgba(243,111,143,0.12)]",
      )}
    >
      {/* w-full: antd centres a Button's content, this keeps the row edge to edge. */}
      <div className="flex w-full items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="!mb-0 break-words text-[15px] font-bold leading-5 text-[#180047]">{diagnosis.name}</p>
            <span className={TAG}>{GROUP_LABEL[groupOf(diagnosis)]}</span>
          </div>
          <p className="!mb-0 !mt-2.5 text-[13px] leading-4 text-[#6B7280]">ICD-10: {diagnosis.icdCode || "Not available"}</p>
        </div>
        <SinceAndUpdated since={diagnosis.diagnosedDate} updated={diagnosis.lastUpdated} />
      </div>
    </Button>
  );
}

// ── Right detail panel ─────────────────────────────────────────────────────
function DetailPanel({ diagnosis, className, onSetStatus, canEditStatus = true }: {
  diagnosis: Diagnosis; className?: string;
  onSetStatus?: (id: string, status: ConditionStatus) => Promise<void>;
  canEditStatus?: boolean;
}) {
  const recordedBy = diagnosis.diagnosedBy === "you" ? "you" : diagnosis.diagnosedBy;
  // Only real events from the record: when it was diagnosed, and its last change.
  const timeline = [
    diagnosis.diagnosedDate
      ? { date: formatDate(diagnosis.diagnosedDate), event: "Diagnosed", detail: recordedBy ? `Recorded by ${recordedBy}` : "Onset recorded" }
      : null,
    diagnosis.lastUpdated
      ? { date: formatDate(diagnosis.lastUpdated), event: "Last Updated", detail: "Most recent change to this record" }
      : null,
  ].filter((x): x is { date: string; event: string; detail: string } => x !== null);
  const related = diagnosis.relatedRecords.length;

  return (
    <div className={cn("rounded-xl border border-[#E5E7EB] bg-white p-[14px]", className)}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-lg bg-[#F17893]">
            <Icon icon="lucide:heart-plus" className="h-9 w-9 text-white" />
          </div>
          <div className="min-w-0 pt-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="!mb-0 break-words text-[17px] font-bold leading-5 text-[#180047]">{diagnosis.name}</h2>
              <span className={TAG}>{GROUP_LABEL[groupOf(diagnosis)]}</span>
              <FoundInRecordsTag duplicates={diagnosis.duplicates} />
              <GoldenTag golden={diagnosis.golden} />
            </div>
            <p className="!mb-0 !mt-2 text-[13px] leading-4 text-[#6B7280]">Since {formatDate(diagnosis.diagnosedDate)}</p>
            <GoldenSources golden={diagnosis.golden} className="mt-2" />
          </div>
        </div>
      </div>

      <ConditionStatusControl
        className="mt-4"
        info={diagnosis.statusInfo}
        canEdit={canEditStatus}
        onChange={onSetStatus ? (s) => onSetStatus(diagnosis.id, s) : undefined}
      />

      {diagnosis.description && (
        <p className="!mb-0 !mt-4 text-xs leading-[21px] text-[#4B5563]">{diagnosis.description}</p>
      )}

      {/* Timeline */}
      <h3 className="!mb-0 !mt-5 text-sm font-bold text-[#180047]">Timeline</h3>
      {timeline.length > 0 ? (
        <div className="mt-6 grid gap-10">
          {timeline.map((item, index) => {
            const isLast = index === timeline.length - 1;
            return (
              <div key={index} className="relative flex h-12 items-center">
                {/* Line from this dot down to the next one (row h-12 + gap-10). */}
                {!isLast && <span aria-hidden="true" className="absolute left-4 top-1/2 h-[88px] w-px bg-[#D1D5DB]" />}
                <div className="flex w-8 shrink-0 justify-center">
                  <span className={cn(
                    "relative rounded-full",
                    isLast
                      ? "h-2.5 w-2.5 bg-[#180047] ring-4 ring-[#E5E7EB]"
                      : "h-2 w-2 bg-[#E4002B] ring-4 ring-[#FDE2E7]",
                  )} />
                </div>
                <p className="!mb-0 !ml-12 w-[110px] shrink-0 text-[13px] font-medium text-[#4B5563]">{item.date}</p>
                <div className="min-w-0 flex-1 text-center">
                  <p className="!mb-0 text-xs text-[#6B7280]">{item.event}</p>
                  <p className="!mb-0 !mt-2 text-xs text-[#E4002B]">{item.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="!mb-0 !mt-3 text-xs text-[#9CA3AF]">No dates were included with this record.</p>
      )}

      {/* Quick Facts */}
      <h3 className="!mb-0 !mt-8 text-sm font-bold text-[#180047]">Quick Facts</h3>
      <div className="mt-3 flex flex-wrap items-start gap-6">
        <div className="min-w-[108px] rounded-lg border border-[#E5E7EB] px-3 py-2.5 shadow-sm">
          <p className="!mb-0 text-[13px] leading-4 text-[#180047]">Diagnosed on</p>
          <p className="!mb-0 !mt-2.5 flex items-center gap-1.5 text-[10px] font-semibold text-[#180047]">
            <span className="h-1 w-1 shrink-0 rounded-full bg-[#180047]" />
            {formatDate(diagnosis.diagnosedDate)}
          </p>
        </div>
        <div className="px-1 py-1">
          <p className="!mb-0 text-xs text-[#6B7280]">Last Update</p>
          <p className="!mb-0 !mt-4 text-center text-xs font-bold text-[#180047]">
            {diagnosis.lastUpdated ? formatRelativeDay(diagnosis.lastUpdated) : "Not available"}
          </p>
        </div>
      </div>

      {/* Related Records */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E5E7EB] p-[14px]">
        <div>
          <p className="!mb-0 text-[13px] font-semibold text-[#180047]">Related Records</p>
          <p className="!mb-0 !mt-2 text-[11px] text-[#4B5563]">
            {related} record{related !== 1 ? "s" : ""} linked to this diagnosis.
          </p>
        </div>
        <Button
          variant="outline"
          disabled
          className="!flex !h-9 !items-center !gap-2 !rounded-lg !border-transparent !bg-[#E5E7EB] !px-3 !text-xs !font-medium !text-[#6B7280]"
        >
          <Icon icon="lucide:files" className="h-4 w-4" />
          View Related Records
        </Button>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export function DiagnosisSection({ diagnoses, isLoading = false, onSetStatus, canEditStatus = true, focus }: DiagnosisSectionProps) {
  const wide = useMediaQuery(LG_QUERY);
  const [filter, setFilter] = useState<Group>("active");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const grouped: Record<Group, Diagnosis[]> = { active: [], inactive: [], resolved: [] };
  for (const d of diagnoses) grouped[groupOf(d)].push(d);
  const shown = grouped[filter];
  const current = GROUPS.find((g) => g.key === filter)!;
  // The panel always shows one diagnosis: the picked one, else the first of the list.
  const selected = shown.find((d) => d.id === selectedId) ?? shown[0] ?? null;

  const select = (d: Diagnosis) => {
    setSelectedId(d.id);
    if (!wide) setModalOpen(true);
  };
  const changeFilter = (next: Group) => {
    setFilter(next);
    setSelectedId(null);
  };
  // A header search result: its group, then the diagnosis itself.
  useRevealSearchTarget(focus, (id) => diagnoses.some((d) => d.id === id), (id) => {
    const d = diagnoses.find((x) => x.id === id);
    if (!d) return;
    setFilter(groupOf(d));
    select(d);
  });

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="mb-8 grid gap-5 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-[#F0F1F3] bg-white p-4 shadow-sm">
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
        <div className="flex items-start gap-8">
          <div className="flex-1 space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border border-[#E5E7EB] bg-white p-4">
                <Skeleton active paragraph={{ rows: 1 }} />
              </div>
            ))}
          </div>
          <div className="hidden flex-1 rounded-xl border border-[#E5E7EB] bg-white p-5 lg:block">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        </div>
      </div>
    );
  }

  if (diagnoses.length === 0) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <EmptyState
          icon={<Icon icon="lucide:stethoscope" className="h-12 w-12 text-[#9CA3AF]" />}
          title="No diagnoses recorded"
          description="Your diagnoses will appear here once added to your records"
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Stat cards */}
      <div className="mb-6 grid gap-5 sm:grid-cols-3">
        {GROUPS.map((g) => (
          <StatCard key={g.key} label={g.stat} icon={g.icon} value={grouped[g.key].length} onClick={() => changeFilter(g.key)} />
        ))}
      </div>

      {/* The platform's splitter (same as Health Concerns): picks which diagnoses are listed. */}
      <div className="mb-6 flex justify-center">
        <ThemedSegmented
          selectedBg="#180047"
          itemColor="#6B7280"
          trackPadding={5}
          value={filter}
          onChange={(v) => changeFilter(v as Group)}
          options={GROUPS.map((g) => ({
            value: g.key,
            label: <span className="block px-2 py-1 text-sm font-semibold">{GROUP_LABEL[g.key]} ({grouped[g.key].length})</span>,
          }))}
          className="!rounded-full !border !border-[#180047] [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-thumb]:!rounded-full"
        />
      </div>

      {shown.length === 0 ? (
        // Nothing in this group: one card across the full width.
        <EmptyState
          icon={<Icon icon={current.icon} className="h-8 w-8 text-[#9CA3AF]" />}
          title={`No ${GROUP_LABEL[filter].toLowerCase()} diagnoses`}
          description={`${GROUP_LABEL[filter]} diagnoses from your records will show up here.`}
          className="min-h-[300px]"
        />
      ) : (
        <div className="flex items-start gap-8">
          {/* Left: this group's diagnoses */}
          <section className="w-full min-w-0 flex-1">
            <h2 className="!mb-4 text-[17px] font-bold text-[#180047]">{current.title}</h2>
            <div className="grid gap-4">
              {shown.map((d) => (
                <div key={d.id} data-search-id={d.id}>
                  <DiagnosisCard diagnosis={d} selected={selected?.id === d.id} onClick={() => select(d)} />
                </div>
              ))}
            </div>
          </section>

          {/* Right: the selected diagnosis, always open (first one by default). Sticky so it stays in view. */}
          {selected && (
            <div className="sticky top-5 hidden flex-1 lg:block">
              <DetailPanel diagnosis={selected} className="min-h-[calc(100vh-300px)]" onSetStatus={onSetStatus} canEditStatus={canEditStatus} />
            </div>
          )}
        </div>
      )}

      {/* Below lg: the tapped diagnosis in a popup (closes on outside click / Escape). */}
      {!wide && selected && (
        <AppModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          width={640}
          hideCloseButton
          className="[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-content]:!shadow-none"
        >
          <DetailPanel diagnosis={selected} onSetStatus={onSetStatus} canEditStatus={canEditStatus} />
        </AppModal>
      )}
    </div>
  );
}
